import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { BookingLinkPanel, GcalStatusBadge, bookingUrl, useGcalReturnToast } from '../components/booking/BookingLinkPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Save, ExternalLink, Briefcase, Settings as SettingsIcon, ListChecks, Users, Calendar, Loader2, GripVertical, Copy } from 'lucide-react';
import { toast } from 'sonner';
import ImagePicker from '../components/cms/ImagePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ISO instant -> value for <input type="datetime-local"> in the browser's zone.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function CareersAdmin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'site';
  const setTab = (t) => setParams({ tab: t }, { replace: true });
  useGcalReturnToast();
  const tabs = [
    { id: 'site',         label: 'Site Settings',         Icon: SettingsIcon },
    { id: 'jobs',         label: 'Job Positions',         Icon: Briefcase },
    { id: 'questions',    label: 'Application Questions', Icon: ListChecks },
    { id: 'interviewers', label: 'Interviewers',          Icon: Users },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin">
          <Button variant="ghost" size="sm"><ArrowLeft size={14} className="mr-1" /> Back to Admin</Button>
        </Link>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="open-public-site-btn">
              <ExternalLink size={14} className="mr-1" /> Open public site
            </Button>
          </a>
          <Link to="/admin/interview-calendar">
            <Button size="sm" data-testid="open-interview-calendar-btn">
              <Calendar size={14} className="mr-1" /> Interview Calendar
            </Button>
          </Link>
        </div>
      </div>

      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Briefcase className="text-primary" /> Careers & Public Site
        </h1>
        <p className="text-muted-foreground mt-1">Edit your public site, manage open positions, and configure the application form.</p>
      </div>

      <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-fit">
        {tabs.map(t => {
          const Icon = t.Icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${tab === t.id ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`careers-tab-${t.id}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'site'         && <SiteSettingsTab />}
      {tab === 'jobs'         && <JobPositionsTab />}
      {tab === 'questions'    && <ApplicationQuestionsTab />}
      {tab === 'interviewers' && <InterviewersTab />}
    </div>
  );
}

// ------------- Site Settings -------------
function SiteSettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/site-settings`).then(r => setS(r.data));
  }, []);
  if (!s) return <Card className="p-6 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2" /> Loading…</Card>;

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, s);
      toast.success('Saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 space-y-4 max-w-3xl">
      <h3 className="font-semibold">Public-facing copy & branding</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Company Name</Label><Input value={s.company_name || ''} onChange={e => set('company_name', e.target.value)} data-testid="site-company-name" /></div>
        <div><Label>Tagline</Label><Input value={s.tagline || ''} onChange={e => set('tagline', e.target.value)} /></div>
        <div className="md:col-span-2"><Label>Hero heading</Label><Input value={s.hero_heading || ''} onChange={e => set('hero_heading', e.target.value)} data-testid="site-hero-heading" /></div>
        <div className="md:col-span-2"><Label>Hero subheading</Label><Input value={s.hero_subheading || ''} onChange={e => set('hero_subheading', e.target.value)} /></div>
        <div>
          <Label>Primary color</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={s.primary_color || '#1a3a5c'} onChange={e => set('primary_color', e.target.value)} className="h-10 w-16 rounded border" data-testid="site-primary-color" />
            <Input value={s.primary_color || ''} onChange={e => set('primary_color', e.target.value)} className="flex-1" />
          </div>
        </div>
        <div>
          <Label>Accent color</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={s.accent_color || '#22c55e'} onChange={e => set('accent_color', e.target.value)} className="h-10 w-16 rounded border" />
            <Input value={s.accent_color || ''} onChange={e => set('accent_color', e.target.value)} className="flex-1" />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>About blurb</Label>
          <textarea rows={3} value={s.about_blurb || ''} onChange={e => set('about_blurb', e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm resize-y min-h-[80px]" />
        </div>
        <div className="md:col-span-2">
          <Label>Careers blurb</Label>
          <textarea rows={2} value={s.careers_blurb || ''} onChange={e => set('careers_blurb', e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm resize-y min-h-[60px]" />
        </div>
        <div><Label>Contact email</Label><Input value={s.contact_email || ''} onChange={e => set('contact_email', e.target.value)} /></div>
        <div><Label>Contact phone</Label><Input value={s.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} /></div>
        <div className="md:col-span-2 pt-4 border-t">
          <Label className="text-base font-semibold">Logo</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Shown in the top-left of every public page and on application emails. Leave blank to display the company initial in a colored square instead.
          </p>
          <ImagePicker
            value={s.logo_url}
            onChange={(v) => set('logo_url', v)}
            placeholder="Paste a logo URL or upload (PNG/SVG/WebP recommended)"
            testId="site-logo"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-base font-semibold">Favicon</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Tiny icon shown in browser tabs. Use a small square image (32×32 or 64×64).
          </p>
          <ImagePicker
            value={s.favicon_url}
            onChange={(v) => set('favicon_url', v)}
            placeholder="Paste a favicon URL or upload"
            testId="site-favicon"
          />
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="save-site-settings-btn">
        <Save size={14} className="mr-1" />
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </Card>
  );
}

// ------------- Job Positions -------------
function JobPositionsTab() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const fetchJobs = async () => {
    const r = await axios.get(`${API}/admin/job-positions`);
    setJobs(r.data);
  };
  useEffect(() => { fetchJobs(); }, []);

  const blank = () => ({ title: '', discipline: '', location: '', employment_type: 'Full-time', summary: '', description: '', requirements: '', is_active: true });

  const save = async () => {
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/job-positions/${editing.id}`, editing);
      } else {
        await axios.post(`${API}/admin/job-positions`, editing);
      }
      toast.success('Saved');
      setEditing(null);
      fetchJobs();
    } catch (e) { toast.error('Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    await axios.delete(`${API}/admin/job-positions/${id}`);
    fetchJobs();
  };

  // Pre-fill the editor with a copy of an existing posting (no id => Save
  // creates a brand-new one). Starts inactive so it isn't published before review.
  const duplicate = (j) => {
    setEditing({
      title: `${j.title} (Copy)`,
      discipline: j.discipline || '',
      location: j.location || '',
      employment_type: j.employment_type || 'Full-time',
      summary: j.summary || '',
      description: j.description || '',
      requirements: j.requirements || '',
      is_active: false,
    });
    toast.info('Duplicated — review and Save to publish.');
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">{jobs.length} position(s)</h3>
        <Button size="sm" onClick={() => setEditing(blank())} data-testid="add-job-btn"><Plus size={14} className="mr-1" /> Add Position</Button>
      </div>

      {editing && (
        <div className="border rounded-lg p-4 mb-4 bg-muted/20 space-y-3">
          <h4 className="font-medium">{editing.id ? 'Edit position' : 'New position'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Title (e.g., Physical Therapist – Home Health)" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="job-title-input" />
            <Input placeholder="Discipline (PT, OT, SLP)" value={editing.discipline || ''} onChange={e => setEditing({ ...editing, discipline: e.target.value })} />
            <Input placeholder="Location" value={editing.location || ''} onChange={e => setEditing({ ...editing, location: e.target.value })} />
            <select value={editing.employment_type || 'Full-time'} onChange={e => setEditing({ ...editing, employment_type: e.target.value })} className="h-10 rounded-md border px-3 text-sm">
              <option>Full-time</option><option>Part-time</option><option>PRN</option><option>Contract</option>
            </select>
            <Input className="md:col-span-2" placeholder="Short summary (1 line shown on careers list)" value={editing.summary || ''} onChange={e => setEditing({ ...editing, summary: e.target.value })} />
            <textarea rows={4} placeholder="Full description" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className="md:col-span-2 rounded-md border px-3 py-2 text-sm resize-y" />
            <textarea rows={3} placeholder="Requirements" value={editing.requirements || ''} onChange={e => setEditing({ ...editing, requirements: e.target.value })} className="md:col-span-2 rounded-md border px-3 py-2 text-sm resize-y" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.is_active !== false} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} /> Active (visible on public site)
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} data-testid="save-job-btn"><Save size={14} className="mr-1" /> Save</Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {jobs.map(j => (
          <div key={j.id} className="flex items-center justify-between border rounded-md p-3 hover:bg-muted/30">
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">
                {j.title}
                {!j.is_active && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">Inactive</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {[j.discipline, j.location, j.employment_type].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(j)} data-testid={`edit-job-${j.id}`}>Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => duplicate(j)} data-testid={`duplicate-job-${j.id}`} title="Duplicate this posting"><Copy size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(j.id)} className="text-red-500"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
        {jobs.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No positions yet. Click "Add Position" above.</p>}
      </div>
    </Card>
  );
}

// ------------- Application Questions -------------
function ApplicationQuestionsTab() {
  const [qs, setQs] = useState([]);
  const fetchQs = async () => setQs((await axios.get(`${API}/admin/application-questions`)).data);
  useEffect(() => { fetchQs(); }, []);

  const update = async (q, patch) => {
    await axios.put(`${API}/admin/application-questions/${q.id}`, { ...q, ...patch });
    fetchQs();
  };
  const add = async () => {
    await axios.post(`${API}/admin/application-questions`, { label: 'New question', field_type: 'text', required: false, sort_order: qs.length, is_active: true });
    fetchQs();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    await axios.delete(`${API}/admin/application-questions/${id}`);
    fetchQs();
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold">Application Questions</h3>
          <p className="text-xs text-muted-foreground">These appear on every job's apply form. Required questions block submission until answered.</p>
        </div>
        <Button size="sm" onClick={add} data-testid="add-question-btn"><Plus size={14} className="mr-1" /> Add Question</Button>
      </div>

      <div className="space-y-2">
        {qs.map(q => (
          <div key={q.id} className="border rounded-md p-3 space-y-2 bg-background">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <GripVertical size={16} className="text-muted-foreground hidden md:block" />
              <Input className="md:col-span-4" value={q.label} onChange={e => setQs(qs.map(x => x.id === q.id ? { ...x, label: e.target.value } : x))} onBlur={() => update(q, { label: q.label })} data-testid={`q-label-${q.id}`} />
              <select value={q.field_type} onChange={e => update(q, { field_type: e.target.value })} className="md:col-span-2 h-10 rounded-md border px-2 text-sm">
                <option value="text">Text</option>
                <option value="textarea">Textarea</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="select">Select</option>
                <option value="checkbox">Checkbox</option>
                <option value="file">File</option>
              </select>
              <Input className="md:col-span-3" placeholder="Placeholder" value={q.placeholder || ''} onChange={e => setQs(qs.map(x => x.id === q.id ? { ...x, placeholder: e.target.value } : x))} onBlur={() => update(q, { placeholder: q.placeholder })} />
              <label className="md:col-span-1 flex items-center gap-1 text-xs"><input type="checkbox" checked={q.required} onChange={e => update(q, { required: e.target.checked })} /> Req</label>
              <label className="md:col-span-1 flex items-center gap-1 text-xs"><input type="checkbox" checked={q.is_active !== false} onChange={e => update(q, { is_active: e.target.checked })} /> Live</label>
              <Button size="sm" variant="ghost" onClick={() => del(q.id)} className="text-red-500 md:col-span-1"><Trash2 size={14} /></Button>
            </div>
            {(q.field_type === 'select' || q.field_type === 'checkbox') && (
              <div>
                <Input
                  placeholder="Options, comma-separated"
                  value={(q.options || []).join(', ')}
                  onChange={e => setQs(qs.map(x => x.id === q.id ? { ...x, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } : x))}
                  onBlur={() => update(q, { options: q.options })}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ------------- Interviewers -------------
function InterviewersTab() {
  const [personnel, setPersonnel] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editAvail, setEditAvail] = useState({ availability: [], blocks: [] });
  const [bookingFor, setBookingFor] = useState(null);

  const refresh = async () => {
    const [pRes, iRes] = await Promise.all([
      axios.get(`${API}/personnel?status=Active`),
      axios.get(`${API}/admin/interviewers`),
    ]);
    setPersonnel(pRes.data);
    setInterviewers(iRes.data);
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async (p) => {
    const want = !(p.is_interviewer);
    await axios.put(`${API}/admin/personnel/${p.id}/interviewer-flag`, { is_interviewer: want });
    toast.success(`${p.first_name} ${p.last_name} ${want ? 'is now' : 'is no longer'} an interviewer`);
    refresh();
  };

  const editFormRef = useRef(null);

  const openAvail = async (p) => {
    try {
      const r = await axios.get(`${API}/admin/personnel/${p.id}/availability`);
      setEditAvail(r.data);
      setEditing(p);
      // Scroll the edit form into view (key on small screens where the layout collapses to 1 column)
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load availability');
    }
  };
  const saveAvail = async () => {
    try {
      await axios.put(`${API}/admin/personnel/${editing.id}/availability`, editAvail);
      toast.success('Availability saved');
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save availability');
    }
  };

  const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card className="p-5">
        <h3 className="font-semibold mb-2">All personnel</h3>
        <p className="text-xs text-muted-foreground mb-3">Toggle the checkbox to mark someone as available to conduct interviews. Only flagged people show up to applicants.</p>
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {personnel.map(p => {
            const flagged = !!p.is_interviewer;
            return (
              <div key={p.id} className="flex items-center justify-between p-2 border rounded hover:bg-muted/30">
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input type="checkbox" checked={flagged} onChange={() => toggle(p)} data-testid={`interviewer-toggle-${p.id}`} />
                  <span className="font-medium text-sm">{p.first_name} {p.last_name}</span>
                  <span className="text-xs text-muted-foreground">{p.email}</span>
                </label>
                {flagged && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAvail(p); }}
                    data-testid={`set-availability-${p.id}`}
                  >
                    Set availability
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5" ref={editFormRef}>
        <h3 className="font-semibold mb-2">{interviewers.filter(i => i.id).length} active interviewer(s)</h3>
        <div className="space-y-1 text-sm">
          {interviewers.filter(i => i.id).map(i => {
            const wins = i.interviewer_availability || [];
            return (
              <div key={i.id} className="flex items-center justify-between p-2 border rounded">
                <div className="min-w-0">
                  <div className="font-medium">{i.first_name} {i.last_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {wins.length === 0 ? 'No availability set' : `${wins.length} weekly window(s)`} · {i.interviewer_timezone || 'America/Chicago'}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <span className={`text-[11px] rounded-full px-2 py-0.5 ${i.booking_enabled ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-500'}`} data-testid={`booking-status-${i.id}`}>
                      Booking link {i.booking_enabled ? 'on' : 'off'}
                    </span>
                    <GcalStatusBadge status={i.google_calendar} testId={`gcal-status-${i.id}`} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                  {i.booking_enabled && i.booking_slug && (
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(bookingUrl(i.booking_slug)); toast.success('Link copied'); }} data-testid={`copy-booking-link-${i.id}`}><Copy size={13} /></Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setBookingFor(i)} data-testid={`booking-settings-${i.id}`}>Booking link</Button>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openAvail(i); }} data-testid={`edit-availability-${i.id}`}>Edit</Button>
                </div>
              </div>
            );
          })}
          {interviewers.filter(i => i.id).length === 0 && <p className="text-muted-foreground text-center py-3">Toggle people on the left to enable interviewing.</p>}
        </div>
      </Card>
    </div>

    <Dialog open={!!bookingFor} onOpenChange={(o) => { if (!o) { setBookingFor(null); refresh(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-settings-dialog">
        <DialogHeader><DialogTitle>Booking link — {bookingFor?.first_name} {bookingFor?.last_name}</DialogTitle></DialogHeader>
        {bookingFor && <BookingLinkPanel personnelId={bookingFor.id} onChange={refresh} />}
      </DialogContent>
    </Dialog>

    {/* Edit availability modal */}
    <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Availability — {editing?.first_name} {editing?.last_name}</DialogTitle>
        </DialogHeader>
        {editing && (
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Weekly availability windows</Label>
              <p className="text-xs text-muted-foreground mb-2">Each window is broken into slots of the chosen length, in the interviewer's timezone ({editing.interviewer_timezone || 'America/Chicago'}). Bookings only land inside these windows.</p>
              <div className="space-y-2">
                {(editAvail.availability || []).map((w, idx) => (
                  <div key={w.id || idx} className="flex items-center gap-2 flex-wrap">
                    <select value={w.day_of_week} onChange={e => {
                      const arr = [...editAvail.availability];
                      arr[idx] = { ...arr[idx], day_of_week: parseInt(e.target.value) };
                      setEditAvail({ ...editAvail, availability: arr });
                    }} className="h-8 rounded-md border px-2 text-xs">
                      {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                    <input type="time" value={w.start_time} onChange={e => {
                      const arr = [...editAvail.availability]; arr[idx].start_time = e.target.value; setEditAvail({ ...editAvail, availability: arr });
                    }} className="h-8 rounded-md border px-2 text-xs" />
                    <span className="text-xs">to</span>
                    <input type="time" value={w.end_time} onChange={e => {
                      const arr = [...editAvail.availability]; arr[idx].end_time = e.target.value; setEditAvail({ ...editAvail, availability: arr });
                    }} className="h-8 rounded-md border px-2 text-xs" />
                    <select value={w.slot_minutes} onChange={e => {
                      const arr = [...editAvail.availability]; arr[idx].slot_minutes = parseInt(e.target.value); setEditAvail({ ...editAvail, availability: arr });
                    }} className="h-8 rounded-md border px-2 text-xs">
                      {[15, 30, 45, 60].map(m => <option key={m} value={m}>{m} min slots</option>)}
                    </select>
                    <Button size="sm" variant="ghost" onClick={() => {
                      const arr = editAvail.availability.filter((_, i) => i !== idx);
                      setEditAvail({ ...editAvail, availability: arr });
                    }} className="text-red-500"><Trash2 size={12} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => {
                  setEditAvail({
                    ...editAvail,
                    availability: [...(editAvail.availability || []), { day_of_week: 0, start_time: '09:00', end_time: '12:00', slot_minutes: 30 }],
                  });
                }}><Plus size={12} className="mr-1" /> Add window</Button>
              </div>
            </div>

            <div>
              <Label className="text-sm">One-off blocks (vacation, meetings)</Label>
              <p className="text-xs text-muted-foreground mb-2">Slots inside any block are removed from the booking calendar.</p>
              <div className="space-y-2">
                {(editAvail.blocks || []).map((b, idx) => (
                  <div key={b.id || idx} className="flex items-center gap-2 flex-wrap">
                    <input type="datetime-local" value={toLocalInput(b.starts_at)} onChange={e => {
                      const arr = [...editAvail.blocks]; arr[idx].starts_at = new Date(e.target.value).toISOString(); setEditAvail({ ...editAvail, blocks: arr });
                    }} className="h-8 rounded-md border px-2 text-xs" />
                    <span className="text-xs">to</span>
                    <input type="datetime-local" value={toLocalInput(b.ends_at)} onChange={e => {
                      const arr = [...editAvail.blocks]; arr[idx].ends_at = new Date(e.target.value).toISOString(); setEditAvail({ ...editAvail, blocks: arr });
                    }} className="h-8 rounded-md border px-2 text-xs" />
                    <Input placeholder="Reason" value={b.reason || ''} onChange={e => {
                      const arr = [...editAvail.blocks]; arr[idx].reason = e.target.value; setEditAvail({ ...editAvail, blocks: arr });
                    }} className="h-8 text-xs flex-1" />
                    <Button size="sm" variant="ghost" onClick={() => {
                      setEditAvail({ ...editAvail, blocks: editAvail.blocks.filter((_, i) => i !== idx) });
                    }} className="text-red-500"><Trash2 size={12} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => {
                  const now = new Date();
                  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
                  setEditAvail({
                    ...editAvail,
                    blocks: [...(editAvail.blocks || []), { id: `tmp-${Date.now()}`, starts_at: tomorrow.toISOString(), ends_at: tomorrow.toISOString(), reason: '' }],
                  });
                }}><Plus size={12} className="mr-1" /> Add block</Button>
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={saveAvail} data-testid="save-availability"><Save size={14} className="mr-1" /> Save availability</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
